import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { SUPABASE_ANON_KEY, SUPABASE_URL, missingConfig } from "./config.ts";

let client: SupabaseClient | null = null;

/**
 * Constructed only when there is something to construct it with: the library
 * throws on an empty URL, and that throw used to take the whole boot down.
 * Everything that touches the network goes through here, so nothing else has
 * to know whether a backend exists.
 */
export function supabase(): SupabaseClient {
  if (missingConfig().length) throw new Error("Supabase ei ole määritetty");
  if (!client) {
    client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: "savusauna.auth",
      },
    });
  }
  return client;
}

export function backendConfigured(): boolean {
  return missingConfig().length === 0;
}

export async function currentSession(): Promise<Session | null> {
  if (!backendConfigured()) return null;
  try {
    const { data } = await supabase().auth.getSession();
    return data.session;
  } catch {
    return null;                 // a corrupt stored session is not a crash
  }
}

/**
 * Email and password, not a magic link.
 *
 * Supabase's built-in mail service delivers only to the project's own team
 * addresses and caps at two messages an hour, so a link never arrives for
 * anyone else. Custom SMTP would fix that, but it would not fix the real
 * problem: a magic link makes signing in depend on reading email at that
 * moment, and this app is used at a place with no signal. A password works
 * offline once the session is on the device, and there is nothing to wait for.
 */
export async function signIn(email: string, password: string): Promise<void> {
  const { error } = await supabase().auth.signInWithPassword({ email, password });
  if (error) throw new Error(translate(error.message));
}

export async function signUp(
  email: string, password: string, display_name: string,
): Promise<{ needsConfirmation: boolean }> {
  const { data, error } = await supabase().auth.signUp({
    email, password,
    options: { data: { display_name, locale: "fi" } },
  });
  if (error) throw new Error(translate(error.message));
  return { needsConfirmation: data.session === null };
}

/**
 * The name everyone else in the sauna sees.
 *
 * It lives in three places and all three have to move together: the account's
 * metadata (which every boot reads), the `app_user` row (which the members
 * list reads), and the `author_name` carried on the comments and ratings this
 * person has already written. That last one is why a rename is worth doing
 * properly — a shared archive where an old comment is signed with a name
 * nobody recognises is worse than one that was never renamed.
 */
export async function updateDisplayName(name: string): Promise<void> {
  const clean = name.trim();
  if (!clean || clean.length > 80) throw new Error("Nimen pitää olla 1–80 merkkiä.");
  const sb = supabase();
  const { data, error: who } = await sb.auth.getUser();
  if (who || !data.user) throw new Error("Kirjaudu ensin sisään.");
  const id = data.user.id;

  const { error: meta } = await sb.auth.updateUser({ data: { display_name: clean } });
  if (meta) throw new Error(translate(meta.message));
  const { error: row } = await sb.from("app_user").update({ display_name: clean }).eq("id", id);
  if (row) throw new Error(translate(row.message));

  /* Cosmetic by comparison, and not worth failing the rename over: the name
     is already changed everywhere that matters if these do not land. */
  await sb.from("comment").update({ author_name: clean }).eq("user_id", id);
  await sb.from("rating").update({ author_name: clean }).eq("user_id", id);
}

/** Supabase answers in English; these are the ones a person actually hits. */
function translate(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("invalid login credentials")) return "Väärä sähköposti tai salasana.";
  if (m.includes("email not confirmed")) return "Sähköpostia ei ole vahvistettu. Ota Supabasessa Confirm email pois päältä, tai vahvista osoite.";
  if (m.includes("user already registered")) return "Tili on jo olemassa — kirjaudu sen sijaan.";
  if (m.includes("password should be at least")) return "Salasanan pitää olla vähintään 6 merkkiä.";
  if (m.includes("failed to fetch") || m.includes("networkerror")) return "Ei saatu yhteyttä palvelimeen.";
  return message;
}

export async function signOut(): Promise<void> {
  await supabase().auth.signOut();
}

/**
 * FR-11.12. An expired token blocks syncing, not using. The archive and a
 * whole evening's recording work from the local copy; the queue simply
 * waits. Nothing about being signed out may stop someone recording a heat
 * they are standing in front of.
 */
export function canSync(session: Session | null): boolean {
  return session !== null;
}
